import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";
import { Type, TypeVariant } from "@themes/pollinator/ui/Type";
import { ThemeType } from "@core/types/style.types";
import { ButtonBase } from "@core/ui";

/** Inline spinner for button loading state - matches Loader component style */
const ButtonSpinner = ({ className }: { className?: string }) => (
  <svg
    className={twMerge("h-4 w-4 animate-spin", className)}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 32 32"
    aria-hidden="true"
  >
    <path
      d="M8 16a8 8 0 0 1 16 0"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
);

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "ghost"
  | "link"
  | "link-light"
  | "link-inline"
  | "menu-item";

export type ButtonSize = "default" | "hero";

export type Props = {
  as?: React.ElementType;
  children?: React.ReactNode;
  variant?: ButtonVariant;
  /** Size variant for different contexts */
  size?: ButtonSize;
  className?: string;
  typeClassName?: string;
  disabled?: boolean;
  /** Looks disabled (grayed out, cursor-not-allowed, aria-disabled) but still clickable */
  visuallyDisabled?: boolean;
  /** Loading state - disables button and shows spinner */
  isLoading?: boolean;
  destructive?: boolean;
  iconComponent?: React.ComponentType<{ className?: string }>;
  type?: "button" | "submit" | "reset";
  typeVariant?: TypeVariant;
  theme?: ThemeType;
  layout?: "vertical" | "horizontal";
  iconPosition?: "left" | "right";
  [key: string]: any;
};

/**
 * Styled button component wrapping ButtonBase.
 * Supports multiple variants, themes, and icon positioning.
 *
 * For polymorphic usage (as="a", etc.), falls back to rendering
 * the element directly without ButtonBase.
 */
export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, Props>(
  (
    {
      as = "button",
      children,
      variant = "primary",
      size = "default",
      className,
      typeClassName,
      disabled,
      visuallyDisabled,
      isLoading,
      destructive,
      iconComponent,
      iconPosition = "left",
      theme = "light",
      type = "button",
      typeVariant,
      layout = "horizontal",
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || isLoading;
    const looksDisabled = isDisabled || visuallyDisabled;
    const styles = [
      // default styles
      "flex items-center justify-center cursor-pointer transition-all duration-100",
      "flex-grow-0 whitespace-nowrap",
      
      // size styles
      size === "default" && "p-2",
      size === "hero" && "px-3 py-2",

      // radius styles
      size === "default" && "rounded-full",
      size === "hero" && "rounded-lg",

      // layout styles
      layout === "vertical" && "flex-col",
      layout === "horizontal" && "flex-row",

      // primary light styles
      variant === "primary" &&
        theme === "light" &&
        "bg-brand-800 text-brand-50 border border-brand-800 hover:bg-brand-900 hover:text-brand-100",

      // primary dark styles
      variant === "primary" &&
        theme === "dark" &&
        "bg-brand-50 text-brand-800 border border-brand-50 hover:bg-brand-100 hover:border-brand-100",

      // secondary light styles
      variant === "secondary" &&
        theme === "light" &&
        "bg-brand-50 text-brand-800 border border-brand-800 hover:bg-brand-100",

      // secondary dark styles
      variant === "secondary" &&
        theme === "dark" &&
        "bg-brand-800 text-brand-50 border border-brand-50 hover:bg-brand-700",

      // ghost light styles
      variant === "ghost" &&
        theme === "light" &&
        "bg-transparent text-brand-800 border border-transparent hover:bg-brand-100 hover:border-brand-100",

      // ghost dark styles
      variant === "ghost" &&
        theme === "dark" &&
        "bg-transparent text-brand-50 border border-transparent hover:bg-brand-800 hover:border-brand-700",

      // link light styles
      (variant === "link" ||
        variant === "link-light" ||
        variant === "link-inline") &&
        theme === "light" &&
        "bg-transparent text-brand-800 hover:text-brand-600",

      // link dark styles
      (variant === "link" ||
        variant === "link-light" ||
        variant === "link-inline") &&
        theme === "dark" &&
        "bg-transparent text-brand-50 hover:text-brand-100",

      // link inline styles
      variant === "link-inline" && "p-0 justify-start w-fit gap-2",

      // menu item light styles
      variant === "menu-item" &&
        theme === "light" &&
        "justify-start text-left py-4 bg-transparent text-brand-800 rounded-none hover:bg-brand-100",

      // menu item dark styles
      variant === "menu-item" &&
        theme === "dark" &&
        "justify-start text-left py-4 bg-transparent text-brand-50 rounded-none hover:bg-brand-700",

      // destructive light styles
      destructive &&
        "bg-error text-brand-50 border-error hover:bg-brand-800 hover:border-brand-800",

      // destructive dark styles
      destructive &&
        theme === "dark" &&
        "bg-error-700 text-brand-50 border-error-700 hover:bg-error-800 hover:border-error-800",

      // dark theme ring styles
      theme === "dark" && "with-ring-inverse",

      // disabled styles
      looksDisabled &&
        "bg-brand-100 text-brand-300 border-brand-300 hover:bg-brand-100 hover:border-brand-300 hover:text-brand-300 cursor-not-allowed",

      // dark theme ghost disabled styles
      theme === "dark" &&
        looksDisabled &&
        variant === "ghost" &&
        "bg-brand-800 border-brand-700 text-brand-700 hover:bg-brand-800 hover:border-brand-700 hover:text-brand-700",
    ];

    const defaultTypeVariant = size === "hero" ? "h4" : "h6";
    const buttonTypeVariant = typeVariant || defaultTypeVariant;
    const Icon = iconComponent;

    const mergedClassName = twMerge(
      "group transition-all duration-100 with-ring",
      styles,
      iconPosition === "right" && "flex-row-reverse",
      className
    );

    const buttonContent = (
      <>
        {isLoading ? (
          <ButtonSpinner
            className={twMerge(
              "transition-all duration-100 shrink-0",
              children && "ml-1 mt-0.5"
            )}
          />
        ) : (
          Icon && (
            <Icon
              className={twMerge(
                "h-4 w-4 transition-all duration-100 shrink-0",
                children && "ml-1"
              )}
            />
          )
        )}
        {children && (
          <Type
            className={twMerge(
              "text-left",
              variant !== "link-inline" && "px-2",
              typeClassName
            )}
            as="span"
            variant={buttonTypeVariant}
          >
            {children}
          </Type>
        )}
      </>
    );

    // Use ButtonBase for standard button elements
    if (as === "button") {
      return (
        <ButtonBase
          ref={ref as React.Ref<HTMLButtonElement>}
          type={type}
          disabled={disabled}
          isLoading={isLoading}
          className={mergedClassName}
          aria-disabled={looksDisabled || undefined}
          data-testid={props["data-testid"]}
          {...props}
        >
          {buttonContent}
        </ButtonBase>
      );
    }

    // For polymorphic usage (as="a", etc.), render the element directly
    const Component = as;

    return (
      <Component
        className={mergedClassName}
        type={as === "button" ? type : undefined}
        disabled={isDisabled}
        aria-disabled={looksDisabled || undefined}
        aria-busy={isLoading || undefined}
        ref={ref as any}
        data-testid={props["data-testid"]}
        {...props}
      >
        {buttonContent}
      </Component>
    );
  }
);

export default Button;
